'use client';

import { useCallback, useState } from 'react';

import { isValidAsciiEmail } from '@/lib/utils/email-format';

export type SubscribeStatus = 'idle' | 'submitting' | 'pending' | 'error';
type Source = 'modal' | 'footer';

export function useSubscribe(source: Source) {
  const [status, setStatus] = useState<SubscribeStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (email: string, honeypot = '') => {
      const trimmed = email.trim();
      // Mirrors the server gate in /api/subscribe (#3411): Resend accepts only a
      // single ASCII address, so the permissive auth emailPattern is not enough here.
      if (!isValidAsciiEmail(trimmed)) {
        setError('Please enter a valid email.');
        setStatus('error');
        return;
      }
      setStatus('submitting');
      setError(null);
      try {
        const res = await fetch('/api/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: trimmed, source, company: honeypot }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string };
          setError(data.error ?? 'Something went wrong. Please try again.');
          setStatus('error');
          return;
        }
        setStatus('pending');
      } catch {
        setError('Network error. Please try again.');
        setStatus('error');
      }
    },
    [source]
  );

  return { status, error, submit };
}
